/**
 * The factor-label (dimensional analysis) method as an engine.
 *
 * A **unit expression** is read into `{ unit, exp }` entries: `kg·m/s²` is kg¹ m¹ s⁻².
 * Operators are read left to right, so `J/mol/K` is J·mol⁻¹·K⁻¹, and brackets group:
 * `J/(mol·K)` means the same. An exponent is `^2`, `^-1`, Unicode superscripts (`s²`),
 * or digits glued to the symbol (`cm3`, `s2`) — no unit symbol here contains a digit.
 *
 * A **conversion factor** is an equality, `1 mi = 1609.344 m`. Applied to a quantity it
 * multiplies by a fraction equal to one, in whichever orientation cancels the unit being
 * removed: `1609.344 m / 1 mi` here, `1 mi / 1609.344 m` if the quantity were in metres.
 * The engine chooses the orientation that cancels more of what is currently on the page,
 * records which units cancelled in which factor so a widget can draw the strikethroughs,
 * and reports the units that remain.
 *
 * Powers are applied one factor at a time: to take m² to cm², use `1 m = 100 cm` twice.
 */

export interface UnitEntry {
  unit: string;
  exp: number;
}

export type UnitParse =
  | { ok: true; entries: UnitEntry[] }
  | { ok: false; error: "empty" | "syntax" };

const UNIT_NAME = /^[A-Za-zµμΩ°%'’]+/;
const DIGITS = /^\d+/;
const SUP_MAP: Record<string, string> = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁻": "-",
};
const SUP_RUN = /^[⁰¹²³⁴⁵⁶⁷⁸⁹⁻]+/;
const OPERATORS = new Set(["*", "·", "×", "⋅"]);
const MAX_EXP = 9;
const MAX_LENGTH = 80;

class UnitReader {
  private pos = 0;
  private readonly text: string;
  constructor(text: string) {
    this.text = text;
  }

  get done(): boolean {
    return this.pos >= this.text.length;
  }

  peek(): string {
    return this.text[this.pos] ?? "";
  }

  next(): string {
    const char = this.text[this.pos] ?? "";
    this.pos++;
    return char;
  }

  match(pattern: RegExp): string | null {
    const found = pattern.exec(this.text.slice(this.pos));
    if (!found) {
      return null;
    }
    this.pos += found[0].length;
    return found[0];
  }

  skipSpaces(): void {
    while (this.peek() === " ") {
      this.pos++;
    }
  }
}

class UnitSyntaxError extends Error {}

function readExponent(reader: UnitReader): number {
  if (reader.peek() === "^") {
    reader.next();
    const sign = reader.peek() === "-" ? -1 : 1;
    if (reader.peek() === "-" || reader.peek() === "+") {
      reader.next();
    }
    const digits = reader.match(DIGITS);
    if (digits === null) {
      throw new UnitSyntaxError();
    }
    return sign * Number(digits);
  }
  const sup = reader.match(SUP_RUN);
  if (sup !== null) {
    const ascii = [...sup].map((c) => SUP_MAP[c]).join("");
    const value = Number(ascii);
    if (Number.isNaN(value)) {
      throw new UnitSyntaxError();
    }
    return value;
  }
  const digits = reader.match(DIGITS);
  return digits === null ? 1 : Number(digits);
}

function addEntry(into: UnitEntry[], unit: string, exp: number): void {
  const found = into.find((entry) => entry.unit === unit);
  if (found) {
    found.exp += exp;
  } else {
    into.push({ unit, exp });
  }
}

/** A primary (a unit with exponent, or a bracketed group) as signed entries. */
function readPrimary(reader: UnitReader, depth: number): UnitEntry[] {
  reader.skipSpaces();
  if (reader.peek() === "(") {
    if (depth > 4) {
      throw new UnitSyntaxError();
    }
    reader.next();
    const inner = readSequence(reader, depth + 1);
    reader.skipSpaces();
    if (reader.next() !== ")") {
      throw new UnitSyntaxError();
    }
    const power = readExponentAfterGroup(reader);
    return inner.map((entry) => ({ ...entry, exp: entry.exp * power }));
  }
  const name = reader.match(UNIT_NAME);
  if (name === null) {
    throw new UnitSyntaxError();
  }
  return [{ unit: name, exp: readExponent(reader) }];
}

function readExponentAfterGroup(reader: UnitReader): number {
  return reader.peek() === "^" || SUP_RUN.test(reader.peek())
    ? readExponent(reader)
    : 1;
}

function readSequence(reader: UnitReader, depth: number): UnitEntry[] {
  const out: UnitEntry[] = [];
  let sign = 1;
  let expectTerm = true;
  let sawOperator = false;
  while (!reader.done) {
    reader.skipSpaces();
    const char = reader.peek();
    if (char === ")" || reader.done) {
      break;
    }
    if (char === "/" || OPERATORS.has(char)) {
      if (sawOperator) {
        throw new UnitSyntaxError();
      }
      reader.next();
      sign = char === "/" ? -1 : 1;
      expectTerm = true;
      sawOperator = true;
      continue;
    }
    for (const entry of readPrimary(reader, depth)) {
      addEntry(out, entry.unit, entry.exp * sign);
    }
    sign = 1;
    expectTerm = false;
    sawOperator = false;
  }
  if (expectTerm && out.length > 0) {
    throw new UnitSyntaxError();
  }
  return out;
}

/** Read a unit expression such as `kg·m/s^2` or `J/(mol·K)`. */
export function parseUnits(text: string): UnitParse {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { ok: false, error: "empty" };
  }
  if (trimmed.length > MAX_LENGTH) {
    return { ok: false, error: "syntax" };
  }
  try {
    const reader = new UnitReader(trimmed);
    const entries = readSequence(reader, 0).filter((e) => e.exp !== 0);
    if (
      !reader.done ||
      entries.length === 0 ||
      entries.some((e) => Math.abs(e.exp) > MAX_EXP)
    ) {
      return { ok: false, error: "syntax" };
    }
    return { ok: true, entries };
  } catch (error) {
    if (error instanceof UnitSyntaxError) {
      return { ok: false, error: "syntax" };
    }
    throw error;
  }
}

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function power(unit: string, exp: number): string {
  if (exp === 1) {
    return unit;
  }
  return unit + [...String(exp)].map((d) => SUPERSCRIPTS[Number(d)]).join("");
}

/** `kg·m/s²`, `J/(mol·K)`, `1/s`. */
export function formatUnits(entries: readonly UnitEntry[]): string {
  const top = entries.filter((e) => e.exp > 0).map((e) => power(e.unit, e.exp));
  const bottom = entries
    .filter((e) => e.exp < 0)
    .map((e) => power(e.unit, -e.exp));
  const num = top.length === 0 ? "1" : top.join("·");
  if (bottom.length === 0) {
    return top.length === 0 ? "" : num;
  }
  return bottom.length === 1
    ? `${num}/${bottom[0]}`
    : `${num}/(${bottom.join("·")})`;
}

/** True when two unit expressions are the same dimension-for-dimension (order ignored). */
export function sameUnits(
  a: readonly UnitEntry[],
  b: readonly UnitEntry[]
): boolean {
  const merged = new Map<string, number>();
  for (const e of a) {
    merged.set(e.unit, (merged.get(e.unit) ?? 0) + e.exp);
  }
  for (const e of b) {
    merged.set(e.unit, (merged.get(e.unit) ?? 0) - e.exp);
  }
  return [...merged.values()].every((exp) => exp === 0);
}

/* --------------------------------------------------------------- the chain */

export interface Token {
  unit: string;
  exp: number;
  /** How much of `exp` cancelled against another factor. */
  cancelled: number;
}

export interface Half {
  /** The number shown on this half of the fraction; null for the start (no denominator). */
  value: number | null;
  tokens: Token[];
}

export interface Block {
  kind: "start" | "factor";
  numerator: Half;
  denominator: Half;
}

export interface FactorSpec {
  leftValue: number;
  leftUnits: readonly UnitEntry[];
  rightValue: number;
  rightUnits: readonly UnitEntry[];
}

export type ChainError =
  | { code: "badFactor"; row: number }
  | { code: "noCommonUnit"; row: number };

export type ChainResult =
  | { ok: false; error: ChainError }
  | {
      ok: true;
      value: number;
      blocks: Block[];
      remaining: UnitEntry[];
    };

interface Live {
  token: Token;
  side: "num" | "den";
}

function net(
  plus: readonly UnitEntry[],
  minus: readonly UnitEntry[]
): UnitEntry[] {
  const out: UnitEntry[] = [];
  for (const e of plus) {
    addEntry(out, e.unit, e.exp);
  }
  for (const e of minus) {
    addEntry(out, e.unit, -e.exp);
  }
  return out.filter((e) => e.exp !== 0);
}

function splitHalves(units: readonly UnitEntry[]): {
  num: Token[];
  den: Token[];
} {
  return {
    num: units
      .filter((e) => e.exp > 0)
      .map((e) => ({ unit: e.unit, exp: e.exp, cancelled: 0 })),
    den: units
      .filter((e) => e.exp < 0)
      .map((e) => ({ unit: e.unit, exp: -e.exp, cancelled: 0 })),
  };
}

function available(live: readonly Live[], side: "num" | "den", unit: string) {
  return live
    .filter((l) => l.side === side && l.token.unit === unit)
    .reduce((sum, l) => sum + (l.token.exp - l.token.cancelled), 0);
}

/** How many unit-exponents `num`/`den` tokens would cancel against what is live. */
function cancellable(
  live: readonly Live[],
  num: readonly Token[],
  den: readonly Token[]
): number {
  let total = 0;
  for (const t of num) {
    total += Math.min(t.exp, available(live, "den", t.unit));
  }
  for (const t of den) {
    total += Math.min(t.exp, available(live, "num", t.unit));
  }
  return total;
}

function cancelAgainst(
  live: Live[],
  token: Token,
  opposite: "num" | "den"
): void {
  let left = token.exp;
  for (const l of live) {
    if (left === 0) {
      break;
    }
    if (l.side !== opposite || l.token.unit !== token.unit) {
      continue;
    }
    const room = l.token.exp - l.token.cancelled;
    const take = Math.min(room, left);
    l.token.cancelled += take;
    token.cancelled += take;
    left -= take;
  }
}

interface Orientation {
  chosen: { num: Token[]; den: Token[] };
  top: number;
  bottom: number;
}

/**
 * Orientation A multiplies by right ÷ left, B by left ÷ right; take the one that cancels
 * more of what is on the page, or null if neither cancels anything.
 */
function chooseOrientation(
  live: readonly Live[],
  factor: FactorSpec
): Orientation | null {
  const a = splitHalves(net(factor.rightUnits, factor.leftUnits));
  const b = splitHalves(net(factor.leftUnits, factor.rightUnits));
  const scoreA = cancellable(live, a.num, a.den);
  const scoreB = cancellable(live, b.num, b.den);
  if (scoreA === 0 && scoreB === 0) {
    return null;
  }
  return scoreA >= scoreB
    ? { chosen: a, top: factor.rightValue, bottom: factor.leftValue }
    : { chosen: b, top: factor.leftValue, bottom: factor.rightValue };
}

/**
 * Apply `start` and then each factor in order.
 * `startValue` multiplies through; factors multiply by (numerator ÷ denominator).
 */
export function runChain(
  startValue: number,
  startUnits: readonly UnitEntry[],
  factors: readonly FactorSpec[]
): ChainResult {
  const startHalves = splitHalves(startUnits);
  const blocks: Block[] = [
    {
      kind: "start",
      numerator: { value: startValue, tokens: startHalves.num },
      denominator: { value: null, tokens: startHalves.den },
    },
  ];
  const live: Live[] = [
    ...startHalves.num.map((token) => ({ token, side: "num" as const })),
    ...startHalves.den.map((token) => ({ token, side: "den" as const })),
  ];
  let value = startValue;

  for (const [row, factor] of factors.entries()) {
    if (
      !(
        factor.leftValue !== 0 &&
        factor.rightValue !== 0 &&
        Number.isFinite(factor.leftValue) &&
        Number.isFinite(factor.rightValue)
      )
    ) {
      return { ok: false, error: { code: "badFactor", row } };
    }
    const pick = chooseOrientation(live, factor);
    if (pick === null) {
      return { ok: false, error: { code: "noCommonUnit", row } };
    }
    const { chosen, top, bottom } = pick;
    for (const token of chosen.num) {
      cancelAgainst(live, token, "den");
    }
    for (const token of chosen.den) {
      cancelAgainst(live, token, "num");
    }
    blocks.push({
      kind: "factor",
      numerator: { value: top, tokens: chosen.num },
      denominator: { value: bottom, tokens: chosen.den },
    });
    live.push(
      ...chosen.num.map((token) => ({ token, side: "num" as const })),
      ...chosen.den.map((token) => ({ token, side: "den" as const }))
    );
    value = (value * top) / bottom;
  }

  const remaining: UnitEntry[] = [];
  for (const l of live) {
    const left = l.token.exp - l.token.cancelled;
    if (left > 0) {
      addEntry(remaining, l.token.unit, l.side === "num" ? left : -left);
    }
  }
  return {
    ok: true,
    value,
    blocks,
    remaining: remaining.filter((e) => e.exp !== 0),
  };
}
